import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ArrowRight, XCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  
  // Get reference from Duitku or invoice_id
  const reference = searchParams.get('reference');
  const merchantOrderId = searchParams.get('merchantOrderId');
  const resultCode = searchParams.get('resultCode');
  const invoiceId = searchParams.get('invoice_id');

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customer_token') || localStorage.getItem('token');
      
      // Try to get invoice details
      let invId = invoiceId;
      
      // If we have merchantOrderId (invoice_number), find the invoice
      if (merchantOrderId && !invId) {
        // merchantOrderId is the invoice_number like INV-202601-012066
        const response = await axios.get(
          `${API_URL}/api/v2/invoices/by-number/${merchantOrderId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          invId = response.data.data.id;
        }
      }
      
      if (invId) {
        const response = await axios.get(
          `${API_URL}/api/v2/payments/details/${invId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          setInvoiceData(response.data.data);
          const status = response.data.data.invoice?.status;
          setPaymentStatus(status === 'paid' ? 'success' : 'pending');
        }
      } else {
        // No invoice found, check resultCode from Duitku
        if (resultCode === '00') {
          setPaymentStatus('success');
        } else if (resultCode === '01') {
          setPaymentStatus('pending');
        } else {
          setPaymentStatus('failed');
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      // Default to success if resultCode is 00
      if (resultCode === '00') {
        setPaymentStatus('success');
      } else {
        setPaymentStatus('pending');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPortal = () => {
    // Try to get tenant from URL params first (from Duitku redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const tenantFromUrl = urlParams.get('tenant');
    
    // Fallback to localStorage
    const tenantCode = tenantFromUrl || localStorage.getItem('tenant_code') || 'T06122503';
    
    // Always redirect to customer portal with tenant code
    window.location.href = `https://radius.radiuslink.id/p/${tenantCode}/dashboard`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 dark:bg-slate-900">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center dark:bg-slate-800">
        {/* Status Icon */}
        {paymentStatus === 'success' ? (
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 dark:bg-green-900/40">
            <CheckCircle2 className="w-14 h-14 text-green-500" />
          </div>
        ) : paymentStatus === 'pending' ? (
          <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6 dark:bg-yellow-900/40">
            <Clock className="w-14 h-14 text-yellow-500" />
          </div>
        ) : (
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 dark:bg-red-900/40">
            <XCircle className="w-14 h-14 text-red-500" />
          </div>
        )}

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-800 mb-2 dark:text-slate-200">
          {paymentStatus === 'success' ? 'Pembayaran Berhasil!' : 
           paymentStatus === 'pending' ? 'Menunggu Pembayaran' : 
           'Pembayaran Gagal'}
        </h1>

        {/* Status Badge */}
        <div className="inline-block mb-4">
          <span className={`px-4 py-2 rounded-full text-white font-semibold ${
            paymentStatus === 'success' ? 'bg-green-500' : 
            paymentStatus === 'pending' ? 'bg-yellow-500' : 
            'bg-red-500'
          }`}>
            {paymentStatus === 'success' ? '✓ LUNAS' : 
             paymentStatus === 'pending' ? '⏳ PENDING' : 
             '✗ GAGAL'}
          </span>
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-6 dark:text-slate-300">
          {paymentStatus === 'success' 
            ? 'Terima kasih atas pembayaran Anda. Transaksi telah berhasil diproses.'
            : paymentStatus === 'pending'
            ? 'Silakan selesaikan pembayaran Anda. Status akan diupdate otomatis.'
            : 'Pembayaran tidak berhasil. Silakan coba lagi.'}
        </p>

        {/* Info Box */}
        {paymentStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left dark:bg-green-900/30">
            <div className="flex items-center gap-2 text-gray-700 mb-2 dark:text-slate-200">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>Status invoice sudah <strong>LUNAS</strong></span>
            </div>
            <div className="flex items-center gap-2 text-gray-700 mb-2 dark:text-slate-200">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>Layanan internet Anda sudah <strong>AKTIF</strong></span>
            </div>
            <div className="flex items-center gap-2 text-gray-700 dark:text-slate-200">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>Bukti pembayaran telah dikirim ke email Anda</span>
            </div>
          </div>
        )}

        {paymentStatus === 'pending' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left dark:bg-yellow-900/30">
            <div className="flex items-center gap-2 text-gray-700 mb-2 dark:text-slate-200">
              <Clock className="w-4 h-4 text-yellow-500" />
              <span>Menunggu konfirmasi pembayaran</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700 dark:text-slate-200">
              <Clock className="w-4 h-4 text-yellow-500" />
              <span>Internet akan aktif setelah pembayaran terkonfirmasi</span>
            </div>
          </div>
        )}

        {/* Invoice Info */}
        {invoiceData?.invoice && (
          <div className="text-sm text-gray-500 mb-6 dark:text-slate-400">
            <p>Invoice: {invoiceData.invoice.invoice_number}</p>
            <p>Customer: {invoiceData.customer?.name}</p>
          </div>
        )}

        {/* Back Button */}
        <Button 
          onClick={handleBackToPortal}
          className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
        >
          <ArrowRight className="w-5 h-5" />
          Kembali ke Portal Pelanggan
        </Button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
